import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Trash2, Sparkles, User as UserIcon } from "lucide-react";

const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "CA", label: "Canada" },
  { code: "AU", label: "Australia" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "IN", label: "India" },
  { code: "BR", label: "Brazil" },
  { code: "JP", label: "Japan" },
  { code: "SG", label: "Singapore" },
  { code: "ES", label: "Spain" },
  { code: "IT", label: "Italy" },
  { code: "MX", label: "Mexico" },
  { code: "Other", label: "Other" },
];

export default function ProfileScreen() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [country, setCountry] = useState(user?.country || "");
  const [city, setCity] = useState(user?.city || "");
  const [name, setName] = useState(user?.name || "");
  const [notifPush, setNotifPush] = useState(user?.notif_push ?? true);
  const [notifEmail, setNotifEmail] = useState(user?.notif_email ?? true);
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/users/me", {
        name, country, city, notif_push: notifPush, notif_email: notifEmail,
      });
      setUser(data.user);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not save");
    } finally { setBusy(false); }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const onDelete = async () => {
    try {
      await api.delete("/users/me");
      await logout();
      toast.success("Account deleted");
      navigate("/login", { replace: true });
    } catch { toast.error("Could not delete"); }
  };

  return (
    <Shell section="settings">
      <div
        className="flex flex-col items-center mb-7 fade-up rounded-2xl p-6 relative overflow-hidden"
        data-testid="profile-header"
        style={{
          background: "rgba(8, 14, 10, 0.72)",
          border: "1px solid rgba(76,175,80,0.28)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt="avatar"
            className="w-20 h-20 rounded-full border-2"
            style={{ borderColor: "#4CAF50", boxShadow: "var(--glow-green)" }}
          />
        ) : (
          <div className="w-20 h-20 rounded-full flex items-center justify-center"
               style={{ background: "rgba(8,14,10,0.85)", border: "2px solid #4CAF50", boxShadow: "var(--glow-green)" }}>
            <UserIcon size={32} className="text-[#4CAF50]" />
          </div>
        )}
        <h2
          className="text-xl font-bold mt-3 text-white"
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85), 0 0 22px rgba(76,175,80,0.35)" }}
          data-testid="profile-name"
        >
          {user?.name}
        </h2>
        <p
          className="text-sm font-medium mt-1"
          style={{ color: "rgba(255,255,255,0.92)", textShadow: "0 1px 8px rgba(0,0,0,0.85)" }}
          data-testid="profile-email"
        >
          {user?.email}
        </p>
        <span
          className="glass-pill solid mt-3"
          data-testid="profile-tier-pill"
          style={
            user?.tier === "platinum"
              ? { fontSize: 13, fontWeight: 800, letterSpacing: "0.12em", padding: "6px 14px", color: "#FFD54F", borderColor: "rgba(255,213,79,0.65)", boxShadow: "0 4px 16px rgba(0,0,0,0.55), 0 0 14px rgba(255,213,79,0.35)" }
              : user?.tier === "premium"
              ? { fontSize: 13, fontWeight: 800, letterSpacing: "0.12em", padding: "6px 14px", color: "#A5D6A7", borderColor: "rgba(76,175,80,0.65)", boxShadow: "0 4px 16px rgba(0,0,0,0.55), 0 0 14px rgba(76,175,80,0.35)" }
              : { fontSize: 13, fontWeight: 800, letterSpacing: "0.12em", padding: "6px 14px" }
          }
        >
          {user?.tier?.toUpperCase() || "FREE"}
        </span>
      </div>

      {/* Subscription card */}
      <div className="p-5 mb-5 fade-up stagger-1 rounded-2xl"
           style={
             user?.tier === "platinum"
               ? { background: "rgba(8, 14, 10, 0.72)", border: "1px solid rgba(255,213,79,0.5)", boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 18px rgba(255,213,79,0.22) inset", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }
               : user?.tier === "premium"
               ? { background: "rgba(8, 14, 10, 0.72)", border: "1px solid rgba(76,175,80,0.5)", boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 18px rgba(76,175,80,0.22) inset", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }
               : { background: "rgba(8, 14, 10, 0.72)", border: "1px solid rgba(76,175,80,0.28)", boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }
           }
           data-testid="profile-subscription">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-soft">Subscription</p>
            <p className="text-lg font-bold capitalize mt-0.5">{user?.tier || "free"}</p>
          </div>
          {user?.tier === "free" ? (
            <button onClick={() => navigate("/subscription")} className="btn-amber inline-flex items-center gap-2 text-xs px-4 py-2">
              <Sparkles size={14} /> Upgrade
            </button>
          ) : (
            <button onClick={() => navigate("/subscription")} className="btn-outline text-xs px-4 py-2">Manage</button>
          )}
        </div>
      </div>

      {/* Settings groups with uppercase headers per spec */}
      <div
        className="p-5 mb-4 fade-up stagger-2 rounded-2xl"
        style={{
          background: "rgba(8, 14, 10, 0.72)",
          border: "1px solid rgba(76,175,80,0.28)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <p style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 14 }}>Account</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-soft">Display name</label>
            <input className="glass-input mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div>
            <label className="text-xs text-muted-soft">Country</label>
            <select
              className="glass-input mt-1"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="profile-country-select"
            >
              <option value="">Select country</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code} style={{ background: "#0A0A0A" }}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-soft">City</label>
            <input
              className="glass-input mt-1"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. San Francisco"
              data-testid="profile-city-input"
            />
            <p className="text-[10px] text-muted-soft mt-1.5">
              Unlocks live weather-adapted care tips (Premium/Platinum).
            </p>
          </div>
        </div>
      </div>

      <div
        className="p-5 mb-4 fade-up stagger-3 rounded-2xl"
        style={{
          background: "rgba(8, 14, 10, 0.72)",
          border: "1px solid rgba(76,175,80,0.28)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <p style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 14 }}>Notifications</p>
        <Toggle label="Push notifications" value={notifPush} onChange={setNotifPush} testid="toggle-push" />
        <Toggle label="Email updates" value={notifEmail} onChange={setNotifEmail} testid="toggle-email" />
      </div>

      <button onClick={save} disabled={busy} className="btn-primary w-full mb-5" data-testid="profile-save">
        {busy ? "Saving…" : "Save changes"}
      </button>

      {/* Links */}
      <div
        className="divide-y divide-white/5 mb-5 fade-up stagger-4 rounded-2xl overflow-hidden"
        style={{
          background: "rgba(8, 14, 10, 0.72)",
          border: "1px solid rgba(76,175,80,0.28)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        {[
          { label: "Growth Journal", path: "/journal" },
          { label: "Music Library", path: "/music" },
          { label: "Contact us", path: "/contact" },
          { label: "About & FAQ", path: "/about" },
          { label: "Privacy Policy", path: "/privacy" },
          { label: "Terms of Service", path: "/terms" },
        ].map((l) => (
          <button
            key={l.path}
            onClick={() => navigate(l.path)}
            className="w-full px-5 py-4 text-left text-sm font-medium hover:bg-white/3 transition-colors"
            data-testid={`profile-link-${l.path.slice(1)}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <button
        onClick={onLogout}
        className="w-full mb-3 inline-flex items-center justify-center gap-2 rounded-xl font-bold py-3.5 transition-all hover:brightness-110"
        data-testid="profile-logout"
        style={{
          background: "linear-gradient(135deg, rgba(244,67,54,0.95), rgba(211,47,47,0.95))",
          border: "1.5px solid rgba(255, 138, 128, 0.85)",
          color: "#FFFFFF",
          letterSpacing: "0.04em",
          textShadow: "0 1px 4px rgba(0,0,0,0.55)",
          boxShadow: "0 8px 22px rgba(244, 67, 54, 0.45), 0 0 18px rgba(244, 67, 54, 0.3)",
        }}
      >
        <LogOut size={17} /> Sign out
      </button>

      <button
        onClick={() => setShowDelete(true)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl font-bold py-3.5 transition-all hover:brightness-110"
        data-testid="profile-delete"
        style={{
          background: "rgba(8, 14, 10, 0.72)",
          border: "1.5px solid rgba(244, 67, 54, 0.7)",
          color: "#FF8A80",
          letterSpacing: "0.04em",
          textShadow: "0 1px 4px rgba(0,0,0,0.6)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.55), 0 0 12px rgba(244, 67, 54, 0.25)",
        }}
      >
        <Trash2 size={16} /> Delete account
      </button>

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowDelete(false)}>
          <div onClick={(e) => e.stopPropagation()} className="glass-card-strong p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-2">Delete your account?</h3>
            <p className="text-soft text-sm mb-5">This permanently removes your data. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDelete(false)} className="btn-outline flex-1">Cancel</button>
              <button onClick={onDelete} className="flex-1 rounded-xl bg-red-500 text-white font-semibold py-3"
                      style={{ boxShadow: "var(--glow-red)" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`relative w-11 h-6 rounded-full transition-all ${value ? "bg-[#4CAF50]" : "bg-white/15"}`}
        style={value ? { boxShadow: "var(--glow-green)" } : {}}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`}
        />
      </button>
    </div>
  );
}
